// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@material-ui/core';
import { addMonths, addDays, startOfDay } from 'date-fns';

import { formatDateMMDDYYYY } from 'utils';
import Calendar from '../Calendar';
import Drawer from '../Drawer';
import LinkButton from '../LinkButton';

// for example the maximum available date from Jun 1 2021 will be July 1 2021
// (01/01/2021 + 6 month = 07/10/2021)
const MAXIMUM_DATE_RANGE_LIMIT_IN_MONTH = 6;

const DeliveryDateSelector = ({
  classes,
  currentDate,
  restrictionsMap,
  updateDateHandler,
  isCartMode,
  isCheckoutPage,
  minDeliveryDateShift,
  vendorId,
  getVendorRestrictionsById,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [disabledDates, setDisabledDates] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(currentDate);

  React.useEffect(() => {
    const _disabledDates = restrictionsMap[vendorId]?.map(({ date }) => new Date(date)) ?? [];
    setDisabledDates(_disabledDates);
  }, [restrictionsMap, vendorId]);

  React.useEffect(() => {
    getVendorRestrictionsById(vendorId);
  }, [vendorId]);

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const onDateUpdate = (date) => {
    updateDateHandler(date);
    setSelectedDate(date);
    setIsDrawerOpen(false);
  };

  const disableWeekendDayPredicate = (date) => {
    // All Mondays, Saturdays and Sundays are not available for selection as per requirement
    const defaultRestrictedDayIndexes = [0, 1, 6];
    return defaultRestrictedDayIndexes.includes(date.getDay());
  };

  const renderDrawerContent = () => {
    const currentDate = startOfDay(new Date());
    const nearestDateAvailableToSelect = addDays(currentDate, minDeliveryDateShift);

    return (
      <Calendar
        data-testid="calendar-widget"
        // render a calendar with previously selected date or with current date
        initialDate={selectedDate || currentDate}
        onUpdateClicked={onDateUpdate}
        disabledDates={disabledDates}
        disableDayPredicate={disableWeekendDayPredicate}
        minDate={nearestDateAvailableToSelect}
        maxDate={addMonths(nearestDateAvailableToSelect, MAXIMUM_DATE_RANGE_LIMIT_IN_MONTH)}
        withValidators
      />
    );
  };

  const typographyVariant = isCartMode ? 'body2' : 'body1';

  return (
    <>
      <Box className={classes.container} data-testid="delivery-date-selector">
        <Typography variant={typographyVariant} className={classes.label}>
          Delivery Date:
        </Typography>
        <Typography variant={typographyVariant} data-testid="delivery-date-selector-value">
          {selectedDate && formatDateMMDDYYYY(selectedDate)}
        </Typography>
        {!isCheckoutPage && (
          <LinkButton
            className={classes.editButton}
            onClick={() => setIsDrawerOpen(true)}
            data-testid="delivery-date-selector-edit-btn"
          >
            {selectedDate ? 'Edit' : 'Select'}
          </LinkButton>
        )}
      </Box>

      <Drawer open={isDrawerOpen} onClose={onDrawerClose}>
        {renderDrawerContent()}
      </Drawer>
    </>
  );
};

DeliveryDateSelector.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  currentDate: PropTypes.instanceOf(Date),
  updateDateHandler: PropTypes.func,
  restrictionsMap: PropTypes.shape({}),
  isCartMode: PropTypes.bool,
  isCheckoutPage: PropTypes.bool,
  minDeliveryDateShift: PropTypes.number,
  getVendorRestrictionsById: PropTypes.func,
  vendorId: PropTypes.string,
};

DeliveryDateSelector.defaultProps = {
  classes: {},
  updateDateHandler: () => {},
  currentDate: null,
  restrictionsMap: {},
  minDeliveryDateShift: 1,
  getVendorRestrictionsById: () => {},
};

export default DeliveryDateSelector;
